import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import InlineAbundanceRenderer from '../Chat/InlineAbundanceRenderer'
import { parseCodeBlock, isAbundanceBlock } from '../Chat/abundanceDetect'
import Icon from '../../../../components/atoms/Icon'
import SydButton from '../../../../components/commonDesign/Button'
import FlexRow from '../../../../components/molecules/FlexRow'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import InlineActions from '../Chat/InlineActions'
import SelectableMarkdown from '../Chat/SelectableMarkdown'

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: 'var(--margin-sm)',
    paddingBottom: 'var(--padding-xs)',
    borderBottom: '1px solid var(--color-neutral-700)'
  },
  icon: {
    fontSize: '20px',
    color: 'var(--color-success-500)'
  },
  title: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--text-color-main)'
  },
  content: {
    fontSize: 'var(--font-size-md)',
    color: 'var(--text-color-main)',
    fontWeight: 'var(--font-weight-light)',
    lineHeight: 1.6,
    '& h1': {
      fontSize: 'var(--font-size-2xl)'
    },
    '& p': {
      margin: '0 0 12px 0',
      '&:last-child': {
        margin: 0
      }
    },
    '& ul, & ol': {
      margin: '0 0 12px 0',
      paddingLeft: '24px',
      '&:last-child': {
        margin: 0
      }
    },
    '& li': {
      marginBottom: '4px'
    },
    '& code': {
      backgroundColor: 'var(--surface-bg-color-d1)',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: 'var(--font-family-monospace)'
    },
    '& pre': {
      border: 'var(--border-neutral-thin)',
      backgroundColor: 'var(--surface-bg-color-d2)',
      paddingLeft: 'var(--padding-sm)',
      paddingRight: 'var(--padding-sm)',
      borderRadius: '8px',
      overflow: 'auto',
      margin: '12px 0',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0
      }
    },
    '& blockquote': {
      borderLeft: '3px solid var(--color-primary-500)',
      paddingLeft: '12px',
      margin: '12px 0',
      fontStyle: 'italic',
      color: 'var(--color-neutral-300)'
    },
    '& a': {
      color: 'var(--color-primary-500)',
      textDecoration: 'none',
      fontWeight: 500,
      borderBottom: '1px solid var(--color-primary-500)',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: 'var(--color-primary-400)',
        borderBottomColor: 'var(--color-primary-400)'
      },
      '&:visited': {
        color: 'var(--color-primary-600)',
        borderBottomColor: 'var(--color-primary-600)'
      }
    }
  },
  codeBlock: {
    position: 'relative',
    '& pre': {
      paddingTop: '36px'
    }
  },
  codeHeader: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    right: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  codeLanguage: {
    fontSize: '12px',
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase'
  },
  generating: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(180deg, transparent 10%, var(--color-ai-200) 30%, var(--color-ai-200) 30%, transparent 10%)',
      backgroundSize: '100% 300%',
      opacity: 0.15,
      animation: '$shine 2s linear infinite',
      pointerEvents: 'none'
    }
  },
  '@keyframes shine': {
    from: { backgroundPosition: '0 200%' },
    to: { backgroundPosition: '0 -100%' }
  }
}))

function FinalMessageBlock ({ content, timestamp, onSuggestionClick, message, onReply, isGenerating }) {
  const classes = useStyles()

  // Renderer functions must be reference-stable: ReactMarkdown treats
  // `components.<tag>` as a component type, so new function refs cause React
  // to unmount/remount the subtree (wiping InlineActions' form state).
  const messageRef = useRef(message)
  const onSuggestionClickRef = useRef(onSuggestionClick)
  messageRef.current = message
  onSuggestionClickRef.current = onSuggestionClick

  const renderers = useMemo(() => {
    const handleCodeAction = (code, language) => {
      if (language === 'dql' || language === 'sql') {
        const queryToolPath = '/admin/denali/analysis/catalog/query'
        sessionStorage.setItem('denaliAssistantQuery', code)
        sessionStorage.setItem('denaliAssistantQueryType', language)
        window.open(queryToolPath, '_blank')
      } else {
        navigator.clipboard.writeText(code)
      }
    }

    return {
      pre ({ children, node }) {
        const { language, codeString } = parseCodeBlock(children, node)

        if (isAbundanceBlock(language, codeString)) {
          return <InlineAbundanceRenderer code={codeString} />
        }

        if (language === 'denali-ai-actions') {
          return (
            <InlineActions
              content={codeString}
              onSuggestionClick={onSuggestionClickRef.current}
              message={messageRef.current}
            />
          )
        }

        if (language) {
          return (
            <div className={classes.codeBlock}>
              <div className={classes.codeHeader}>
                <span className={classes.codeLanguage}>{language.split('/')[0]}</span>
                <FlexRow gap='xs'>
                  {(language === 'dql' || language === 'sql') ? (
                    <SydButton
                      size='xs'
                      variant='ghost'
                      onClick={() => handleCodeAction(codeString, language)}
                    >
                      <Icon name='open_in_new' style={{ fontSize: '14px' }} />
                      Open in Editor
                    </SydButton>
                  ) : null}
                  <SydButton
                    size='xs'
                    variant='ghost'
                    onClick={() => navigator.clipboard.writeText(codeString)}
                  >
                    <Icon name='copy' style={{ fontSize: '14px' }} />
                    Copy
                  </SydButton>
                </FlexRow>
              </div>
              <pre className={language ? `language-${language}` : undefined}>
                <code>{codeString}</code>
              </pre>
            </div>
          )
        }

        return <pre>{children}</pre>
      },
      a ({ href, children }) {
        return (
          <a href={href} target='_blank' rel='noopener noreferrer'>
            {children}
            <Icon name='openInNew' style={{ fontSize: '0.85em', verticalAlign: 'middle', marginLeft: '3px', opacity: 0.8 }} />
          </a>
        )
      },
      code ({ inline, children, ...props }) {
        if (inline) {
          return <code {...props}>{children}</code>
        }
        return <code {...props}>{children}</code>
      }
    }
  }, [classes])

  const incompleteSentence = /[.!…]$/.test(content) === false
  const showEllipsis = isGenerating && incompleteSentence

  if (!content) {
    return null
  }

  const displayContent = showEllipsis ? content + '…' : content

  return (
    <FadeIn className={classes.finalMessage}>
      {onReply ? (
        <SelectableMarkdown
          content={displayContent}
          onReply={onReply}
          customRenderers={renderers}
          className={`${classes.content}${isGenerating ? ` ${classes.generating}` : ''}`}
        />
      ) : (
        <div className={`${classes.content}${isGenerating ? ` ${classes.generating}` : ''}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={renderers}
          >
            {displayContent}
          </ReactMarkdown>
        </div>
      )}
    </FadeIn>
  )
}

FinalMessageBlock.propTypes = {
  content: PropTypes.string.isRequired,
  timestamp: PropTypes.string,
  onSuggestionClick: PropTypes.func,
  message: PropTypes.object,
  onReply: PropTypes.func,
  isGenerating: PropTypes.bool
}

export default FinalMessageBlock
